/**
 * O painel de quem administra a instalação. Não é uma tela do app da mãe.
 *
 * Ele fala com `/api/admin` por um cliente próprio, com sessão própria: nada
 * aqui passa pelo `api.ts`, que carrega a identidade da mãe. Manter os dois
 * clientes separados é o que impede que um bug de cabeçalho mande a sessão
 * errada para o lado errado.
 *
 * **Nenhuma credencial é lida de volta.** O servidor não devolve valor, e esta
 * tela não guarda o que foi digitado. Para trocar, digita-se a nova; para
 * conferir se está lá, olha-se "configurada". Campo que mostra a chave é
 * campo que vaza a chave numa captura de tela.
 *
 * **Ninguém precisa de SSH para entrar no próprio sistema.** Antes da sessão
 * esta tela tem quatro estados, e ela escolhe sozinha qual mostrar: criar a
 * conta de operação numa instalação nova, entrar, pedir link de recuperação e
 * escolher senha nova. Antes disso a conta só nascia por linha de comando
 * dentro do servidor e uma senha esquecida não tinha saída nenhuma — o dono do
 * sistema ficava trancado do lado de fora dele.
 */

import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'

import { Icone } from '../componentes/Icone'
import '../estilos/admin.css'

const CHAVE_DA_SESSAO = 'ruth.admin'

type Segredo = {
  chave: string
  configurado: boolean
  origem: string | null
  trocado_em: string | null
}

type Segredos = { cofre_disponivel: boolean; segredos: Segredo[] }

/** O que toda porta de entrada devolve quando dá certo: entrar, criar, redefinir. */
type Entrada = { sessao: string; nome: string; email: string }

type Batimento = {
  status: string
  ha_minutos?: number | null
}

type Casa = {
  usuaria_id: string
  email: string
  nome_tratamento: string
  criada_em: string | null
  ativa: boolean
  se_apresentou: boolean
}

/** O nome de cada credencial em português, para não pedir "whisper_api_key". */
const NOMES: Record<string, string> = {
  openai_api_key: 'OpenAI — a inteligência da Ruth e a leitura da receita',
  whisper_api_key: 'Whisper próprio — só se não for o da OpenAI',
  vapid_public_key: 'Web Push — chave pública',
  vapid_private_key: 'Web Push — chave privada',
  google_client_id: 'Google Agenda — client ID',
  google_client_secret: 'Google Agenda — client secret',
  whatsapp_token: 'WhatsApp — token da Meta',
  whatsapp_phone_number_id: 'WhatsApp — número',
  smtp_host: 'E-mail — servidor',
  smtp_usuario: 'E-mail — usuário',
  smtp_senha: 'E-mail — senha',
}

/**
 * Recusa do servidor, com o status junto.
 *
 * O status importa porque cada um pede outra tela e não outra frase: 409 no
 * primeiro acesso quer dizer que a conta já existe e o lugar de quem está aqui
 * é o login; 400 ao redefinir quer dizer que o link morreu e o formulário de
 * senha não adianta mais. Sem o número, os dois virariam texto vermelho sobre
 * um formulário que nunca vai funcionar.
 */
class ErroDoPainel extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    super(mensagem)
  }
}

async function chamar<T>(caminho: string, sessao: string | null, opcoes: RequestInit = {}) {
  const resposta = await fetch(`/api/admin${caminho}`, {
    ...opcoes,
    headers: {
      'Content-Type': 'application/json',
      ...(sessao ? { Authorization: `Bearer ${sessao}` } : {}),
      ...opcoes.headers,
    },
  })
  if (!resposta.ok) {
    const corpo = await resposta.json().catch(() => ({}))
    throw new ErroDoPainel(resposta.status, detalhe(corpo, `HTTP ${resposta.status}`))
  }
  return resposta.json() as Promise<T>
}

const DADOS_RECUSADOS = 'O servidor não aceitou esses dados. Confere o e-mail e tenta de novo.'

/**
 * O `detail` da recusa, quando ele é uma frase.
 *
 * O FastAPI escreve `detail` de dois jeitos: texto, quando a regra é nossa —
 * "a senha precisa de 12 caracteres" —, e **lista de objetos**, quando quem
 * recusou foi o validador de esquema (um e-mail que o navegador aceita e o
 * `EmailStr` não). Jogar essa lista num `<p>` derruba a tela inteira em cima
 * de um e-mail mal digitado, e o operador fica sem formulário e sem pista.
 */
function detalhe(corpo: unknown, alternativa: string): string {
  const texto = (corpo as { detail?: unknown } | null)?.detail
  if (typeof texto === 'string') return texto
  return texto === undefined ? alternativa : DADOS_RECUSADOS
}

const SEM_RESPOSTA = 'Não consegui falar com o servidor. Tenta de novo daqui a pouco.'

/**
 * O texto que vai para a tela quando uma chamada falha.
 *
 * `ErroDoPainel` é recusa do servidor, e o `detail` dele já vem em português —
 * a frase sobre senha fraca é escrita lá, e é ela que o operador precisa ler.
 * Qualquer outra coisa é o navegador falando: `fetch` sem rede rejeita com
 * "Failed to fetch" no Chrome e "Load failed" no Safari, e nenhuma das duas
 * diz a quem está do lado de cá o que fazer agora.
 */
function frase(erro: unknown, alternativa: string): string {
  return erro instanceof ErroDoPainel ? erro.message : alternativa
}

const SENHAS_DIFERENTES = 'As duas senhas não estão iguais. Confere e tenta de novo.'

/**
 * As duas senhas conferem antes de a tela chamar o servidor.
 *
 * Não é validação duplicada: é a única coisa que só o cliente pode conferir. O
 * servidor recebe uma senha e não tem como saber que ela saiu de um dedo
 * escorregado — e um erro de digitação numa senha que ninguém vê é o jeito
 * mais fácil de o operador se trancar do lado de fora no primeiro minuto.
 */
function conferem(senha: string, repetida: string): boolean {
  return senha === repetida
}

export function Admin() {
  const [sessao, setSessao] = useState<string | null>(() =>
    localStorage.getItem(CHAVE_DA_SESSAO),
  )
  const [nome, setNome] = useState<string | null>(null)

  const guardarSessao = useCallback((token: string) => {
    localStorage.setItem(CHAVE_DA_SESSAO, token)
    setSessao(token)
  }, [])

  const sair = useCallback(() => {
    localStorage.removeItem(CHAVE_DA_SESSAO)
    setSessao(null)
    setNome(null)
  }, [])

  useEffect(() => {
    if (!sessao) return
    chamar<{ nome: string }>('/eu', sessao)
      .then((eu) => setNome(eu.nome))
      // Sessão vencida ou conta desativada: cai para a tela de entrar em vez
      // de deixar o painel meio carregado.
      .catch(sair)
  }, [sessao, sair])

  if (!sessao) {
    return <Porta aoEntrar={guardarSessao} />
  }

  // Sessão guardada e nome a caminho. Mostrar o formulário de login neste vão
  // pediria a senha de novo a quem já está dentro — e quem obedecesse ao
  // pedido veria a tela trocar embaixo do dedo.
  if (!nome) {
    return (
      <Caixa>
        <p className="adm__sub">Um instante.</p>
      </Caixa>
    )
  }

  return <Painel sessao={sessao} nome={nome} aoSair={sair} aoTrocarSessao={guardarSessao} />
}

/* =========================================================== antes da sessão */

/**
 * A porta: quatro estados, e a tela escolhe sozinha qual mostrar.
 *
 * Instalação nova cai em "primeira conta"; link de recuperação na URL cai em
 * "redefinir"; o resto é o login de todo dia, com "esqueci a senha" a um toque.
 * Quem opera nunca escolhe entre eles num menu — em cada uma dessas situações
 * existe uma coisa só a fazer.
 */
type Momento = 'olhando' | 'primeira-conta' | 'entrar' | 'recuperar' | 'redefinir'

const JA_TEM_CONTA =
  'Esta instalação já tem conta de operação. Entre com ela — ou peça uma senha nova, se você não lembra.'

function tokenDeRecuperacao(): string | null {
  return new URLSearchParams(window.location.search).get('recuperar')
}

/**
 * O token sai da barra de endereço assim que a tela o guarda na memória.
 *
 * Ele continua valendo até ser usado, e a barra de endereço é justamente onde
 * um segredo é fotografado, copiado num chat e sincronizado para os outros
 * aparelhos da mesma conta de navegador. Quem recarregar a página sem ele cai
 * no login, que tem "Esqueci a senha" logo ali.
 */
function limparAUrl(): void {
  window.history.replaceState(null, '', window.location.pathname)
}

function Porta({ aoEntrar }: { aoEntrar: (sessao: string) => void }) {
  const [token] = useState(tokenDeRecuperacao)
  const [momento, setMomento] = useState<Momento>(() => (token ? 'redefinir' : 'olhando'))
  const [recado, setRecado] = useState<string | null>(null)

  useEffect(() => {
    if (token) {
      limparAUrl()
      return
    }

    let vivo = true
    chamar<{ precisa: boolean }>('/primeiro-acesso', null)
      .then(({ precisa }) => {
        if (vivo) setMomento(precisa ? 'primeira-conta' : 'entrar')
      })
      // Servidor fora do ar não é instalação nova. Na dúvida mostra o login,
      // que é o caminho de sempre: oferecer "criar a primeira conta" a quem
      // não obteve resposta convidaria a tentar uma conta por cima da que já
      // existe, e a recusa só apareceria depois da senha digitada.
      .catch(() => {
        if (vivo) setMomento('entrar')
      })

    return () => {
      vivo = false
    }
  }, [token])

  const paraOLogin = useCallback((porque: string | null) => {
    setRecado(porque)
    setMomento('entrar')
  }, [])

  if (momento === 'olhando') {
    return (
      <Caixa>
        <p className="adm__sub">Um instante.</p>
      </Caixa>
    )
  }

  if (momento === 'primeira-conta') {
    return <PrimeiraConta aoEntrar={aoEntrar} aoJaExistir={() => paraOLogin(JA_TEM_CONTA)} />
  }

  if (momento === 'recuperar') {
    return <Recuperar aoVoltar={() => paraOLogin(null)} />
  }

  if (momento === 'redefinir' && token) {
    return (
      <Redefinir
        token={token}
        aoEntrar={aoEntrar}
        aoPedirOutro={() => {
          setRecado(null)
          setMomento('recuperar')
        }}
      />
    )
  }

  return (
    <Entrar
      aoEntrar={aoEntrar}
      recado={recado}
      aoEsquecer={() => {
        setRecado(null)
        setMomento('recuperar')
      }}
    />
  )
}

/** A moldura de toda tela anterior à sessão: a marca, e o que vier depois. */
function Caixa({
  aoEnviar,
  children,
}: {
  aoEnviar?: (evento: FormEvent) => void
  children: ReactNode
}) {
  const conteudo = (
    <>
      <h1 className="adm__marca display">Ruth</h1>
      <p className="adm__sub">painel de operação</p>
      {children}
    </>
  )

  return (
    <div className="adm adm--entrar">
      {aoEnviar ? (
        <form className="adm__forma" onSubmit={aoEnviar}>
          {conteudo}
        </form>
      ) : (
        <div className="adm__forma">{conteudo}</div>
      )}
    </div>
  )
}

function Campo({
  rotulo,
  tipo,
  valor,
  preenchimento,
  aoDigitar,
  automatico,
}: {
  rotulo: string
  tipo: 'email' | 'password' | 'text'
  valor: string
  /** O `autocomplete` do navegador. Errar aqui faz o gerenciador de senhas guardar a coisa errada. */
  preenchimento: string
  aoDigitar: (valor: string) => void
  automatico?: boolean
}) {
  return (
    <label className="adm__campo">
      <span>{rotulo}</span>
      <input
        type={tipo}
        autoComplete={preenchimento}
        value={valor}
        onChange={(evento) => aoDigitar(evento.target.value)}
        autoFocus={automatico}
        required
      />
    </label>
  )
}

/**
 * A conta de operação de uma instalação nova.
 *
 * Só aparece quando o servidor diz que não existe nenhuma. Depois da primeira,
 * esta rota recusa — e a tela obedece à recusa voltando para o login, porque
 * insistir no formulário ofereceria o mesmo erro para sempre.
 */
function PrimeiraConta({
  aoEntrar,
  aoJaExistir,
}: {
  aoEntrar: (sessao: string) => void
  aoJaExistir: () => void
}) {
  const [email, setEmail] = useState('')
  const [nome, setNome] = useState('')
  const [senha, setSenha] = useState('')
  const [repetida, setRepetida] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)

  const criar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (!conferem(senha, repetida)) {
      setErro(SENHAS_DIFERENTES)
      return
    }

    setCriando(true)
    setErro(null)
    try {
      const conta = await chamar<Entrada>('/primeiro-acesso', null, {
        method: 'POST',
        body: JSON.stringify({ email, nome, senha }),
      })
      aoEntrar(conta.sessao)
    } catch (e) {
      // 409: a conta nasceu entre a pergunta e o envio — duas abas abertas, ou
      // alguém no servidor pela linha de comando. Quem está aqui não tem o que
      // criar; tem o que entrar.
      if (e instanceof ErroDoPainel && e.status === 409) {
        aoJaExistir()
        return
      }
      // 422 é senha fraca, e a frase vem pronta do servidor: a regra de força
      // mora lá, escrita uma vez. Duplicá-la aqui daria duas verdades sobre a
      // mesma senha, e a de cá envelheceria primeiro.
      setErro(frase(e, 'Não consegui criar a conta.'))
    } finally {
      setCriando(false)
    }
  }

  return (
    <Caixa aoEnviar={criar}>
      <p className="adm__aviso">
        Esta instalação ainda não tem quem opere. Esta é a conta de operação, e ela é criada uma
        vez só: depois desta, aqui vira a tela de entrar.
      </p>

      <Campo
        rotulo="E-mail"
        tipo="email"
        valor={email}
        preenchimento="username"
        aoDigitar={setEmail}
        automatico
      />
      <Campo
        rotulo="Como te chamar"
        tipo="text"
        valor={nome}
        preenchimento="name"
        aoDigitar={setNome}
      />
      <Campo
        rotulo="Senha"
        tipo="password"
        valor={senha}
        preenchimento="new-password"
        aoDigitar={setSenha}
      />
      <Campo
        rotulo="Repita a senha"
        tipo="password"
        valor={repetida}
        preenchimento="new-password"
        aoDigitar={setRepetida}
      />

      {erro && (
        <p className="adm__erro" role="alert">
          {erro}
        </p>
      )}

      <button type="submit" className="adm__entrar" disabled={criando}>
        {criando ? 'Criando…' : 'Criar a conta'}
      </button>
    </Caixa>
  )
}

function Entrar({
  aoEntrar,
  recado,
  aoEsquecer,
}: {
  aoEntrar: (sessao: string) => void
  /** O que trouxe alguém de volta para cá, quando não foi o caminho de sempre. */
  recado: string | null
  aoEsquecer: () => void
}) {
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)

  const entrar = async (evento: FormEvent) => {
    evento.preventDefault()
    setEntrando(true)
    setErro(null)
    try {
      const corpo = await chamar<Entrada>('/entrar', null, {
        method: 'POST',
        body: JSON.stringify({ email, senha }),
      })
      aoEntrar(corpo.sessao)
    } catch (e) {
      setErro(frase(e, SEM_RESPOSTA))
    } finally {
      setEntrando(false)
    }
  }

  return (
    <Caixa aoEnviar={entrar}>
      {recado && (
        <p className="adm__aviso" role="status">
          {recado}
        </p>
      )}

      <Campo
        rotulo="E-mail"
        tipo="email"
        valor={email}
        preenchimento="username"
        aoDigitar={setEmail}
      />
      <Campo
        rotulo="Senha"
        tipo="password"
        valor={senha}
        preenchimento="current-password"
        aoDigitar={setSenha}
      />

      {erro && (
        <p className="adm__erro" role="alert">
          {erro}
        </p>
      )}

      <button type="submit" className="adm__entrar" disabled={entrando}>
        {entrando ? 'Entrando…' : 'Entrar'}
      </button>

      <button type="button" className="adm__link" onClick={aoEsquecer}>
        Esqueci a senha
      </button>
    </Caixa>
  )
}

/**
 * O pedido de senha nova.
 *
 * A resposta é a mesma para conta que existe e para conta que não existe, e a
 * frase da tela também. Um "não conheço esse e-mail" transformaria este campo
 * na lista de quem opera a Ruth: bastaria digitar endereços até um deles não
 * ser recusado. O servidor devolve o mesmo 200 pelo mesmo motivo — a defesa só
 * funciona se os dois lados calarem.
 */
const LINK_A_CAMINHO =
  'Se essa conta existir, o link chega em instantes. Ele vale por pouco tempo e abre uma vez só. Se não aparecer, olhe no spam.'

function Recuperar({ aoVoltar }: { aoVoltar: () => void }) {
  const [email, setEmail] = useState('')
  const [mandado, setMandado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [mandando, setMandando] = useState(false)

  const pedir = async (evento: FormEvent) => {
    evento.preventDefault()
    setMandando(true)
    setErro(null)
    try {
      await chamar<{ mandei: boolean }>('/recuperar', null, {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
      setMandado(true)
    } catch (e) {
      setErro(frase(e, SEM_RESPOSTA))
    } finally {
      setMandando(false)
    }
  }

  if (mandado) {
    return (
      <Caixa>
        <p className="adm__aviso" role="status">
          {LINK_A_CAMINHO}
        </p>
        <button type="button" className="adm__link" onClick={aoVoltar}>
          Voltar para o login
        </button>
      </Caixa>
    )
  }

  return (
    <Caixa aoEnviar={pedir}>
      <p className="adm__aviso">
        Digite o e-mail da conta de operação. Eu mando um link para você escolher outra senha.
      </p>

      <Campo
        rotulo="E-mail"
        tipo="email"
        valor={email}
        preenchimento="username"
        aoDigitar={setEmail}
        automatico
      />

      {erro && (
        <p className="adm__erro" role="alert">
          {erro}
        </p>
      )}

      <button type="submit" className="adm__entrar" disabled={mandando}>
        {mandando ? 'Mandando…' : 'Mandar o link'}
      </button>

      <button type="button" className="adm__link" onClick={aoVoltar}>
        Voltar para o login
      </button>
    </Caixa>
  )
}

/**
 * A senha nova, para quem chegou pelo link do e-mail.
 *
 * Dando certo, entra direto: quem acabou de provar que abre a caixa de entrada
 * da conta não tem o que provar de novo digitando a senha que escolheu há dois
 * segundos.
 */
function Redefinir({
  token,
  aoEntrar,
  aoPedirOutro,
}: {
  token: string
  aoEntrar: (sessao: string) => void
  aoPedirOutro: () => void
}) {
  const [senha, setSenha] = useState('')
  const [repetida, setRepetida] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [morto, setMorto] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (!conferem(senha, repetida)) {
      setErro(SENHAS_DIFERENTES)
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      const conta = await chamar<Entrada>('/redefinir', null, {
        method: 'POST',
        body: JSON.stringify({ token, senha }),
      })
      aoEntrar(conta.sessao)
    } catch (e) {
      // 400 é link vencido, já usado ou inventado — o token morreu e nenhuma
      // senha digitada aqui vai passar. Deixar o formulário de pé convidaria a
      // tentar de novo até desconfiar da senha, que não é o problema.
      if (e instanceof ErroDoPainel && e.status === 400) setMorto(true)
      setErro(frase(e, SEM_RESPOSTA))
    } finally {
      setSalvando(false)
    }
  }

  if (morto) {
    return (
      <Caixa>
        <p className="adm__erro" role="alert">
          {erro}
        </p>
        <button type="button" className="adm__link" onClick={aoPedirOutro}>
          Pedir outro link
        </button>
      </Caixa>
    )
  }

  return (
    <Caixa aoEnviar={salvar}>
      <p className="adm__aviso">Escolha a senha nova. Salvando, você já entra.</p>

      <Campo
        rotulo="Senha nova"
        tipo="password"
        valor={senha}
        preenchimento="new-password"
        aoDigitar={setSenha}
        automatico
      />
      <Campo
        rotulo="Repita a senha"
        tipo="password"
        valor={repetida}
        preenchimento="new-password"
        aoDigitar={setRepetida}
      />

      {erro && (
        <p className="adm__erro" role="alert">
          {erro}
        </p>
      )}

      <button type="submit" className="adm__entrar" disabled={salvando}>
        {salvando ? 'Salvando…' : 'Salvar e entrar'}
      </button>
    </Caixa>
  )
}

/* ================================================================== o painel */

function Painel({
  sessao,
  nome,
  aoSair,
  aoTrocarSessao,
}: {
  sessao: string
  nome: string
  aoSair: () => void
  /** A troca de senha devolve uma sessão nova; o painel guarda e segue. */
  aoTrocarSessao: (token: string) => void
}) {
  const [dados, setDados] = useState<Segredos | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      setDados(await chamar<Segredos>('/segredos', sessao))
      setErro(null)
    } catch (e) {
      setErro(frase(e, 'não consegui ler as credenciais'))
    }
  }, [sessao])

  useEffect(() => {
    void carregar()
  }, [carregar])

  return (
    <div className="adm">
      <header className="adm__cabeca">
        <div>
          <h1 className="adm__marca display">Ruth</h1>
          <p className="adm__sub">painel de operação · {nome}</p>
        </div>
        <button type="button" className="adm__sair" onClick={aoSair}>
          Sair
        </button>
      </header>

      <p className="adm__aviso">
        As credenciais ficam cifradas no servidor e nunca voltam para esta tela — nem para você.
        Para trocar, digite a nova. Para conferir, olhe se está configurada.
      </p>

      {dados && !dados.cofre_disponivel && (
        <p className="adm__erro">
          O servidor está sem <code>CHAVE_CRIPTOGRAFIA</code>. Sem ela não dá para guardar
          credencial em segurança. Gere uma com <code>python -m app.cli gerar-chave</code>, ponha
          no <code>.env</code> e reinicie.
        </p>
      )}

      {erro && <p className="adm__erro">{erro}</p>}

      <OsLembretes />

      <Casas sessao={sessao} />

      <h2 className="adm__secao">Credenciais</h2>

      <div className="adm__lista">
        {dados?.segredos.map((segredo) => (
          <Credencial
            key={segredo.chave}
            segredo={segredo}
            sessao={sessao}
            podeGuardar={dados.cofre_disponivel}
            aoMudar={carregar}
          />
        ))}
      </div>

      <Senha sessao={sessao} aoTrocarSessao={aoTrocarSessao} />
    </div>
  )
}

/**
 * A senha de quem está dentro.
 *
 * A atual é pedida mesmo com a sessão aberta: uma aba esquecida num computador
 * emprestado não pode virar dona da conta. Ao trocar, o servidor derruba as
 * outras sessões e devolve uma nova para esta — a tela guarda a nova e segue,
 * sem pedir para entrar de novo.
 */
function Senha({
  sessao,
  aoTrocarSessao,
}: {
  sessao: string
  aoTrocarSessao: (token: string) => void
}) {
  const [atual, setAtual] = useState('')
  const [nova, setNova] = useState('')
  const [repetida, setRepetida] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [pronto, setPronto] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const trocar = async (evento: FormEvent) => {
    evento.preventDefault()
    setPronto(false)
    if (!conferem(nova, repetida)) {
      setErro(SENHAS_DIFERENTES)
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      const conta = await chamar<Entrada>('/senha', sessao, {
        method: 'PUT',
        body: JSON.stringify({ senha_atual: atual, senha_nova: nova }),
      })
      aoTrocarSessao(conta.sessao)
      // Nada do que ele digitou fica na tela.
      setAtual('')
      setNova('')
      setRepetida('')
      setPronto(true)
    } catch (e) {
      setErro(frase(e, SEM_RESPOSTA))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <section className="adm-senha">
      <h2 className="adm__secao">Senha</h2>

      <form className="adm-senha__forma" onSubmit={trocar}>
        <Campo
          rotulo="Senha atual"
          tipo="password"
          valor={atual}
          preenchimento="current-password"
          aoDigitar={setAtual}
        />
        <Campo
          rotulo="Senha nova"
          tipo="password"
          valor={nova}
          preenchimento="new-password"
          aoDigitar={setNova}
        />
        <Campo
          rotulo="Repita a senha nova"
          tipo="password"
          valor={repetida}
          preenchimento="new-password"
          aoDigitar={setRepetida}
        />

        {erro && (
          <p className="adm__erro" role="alert">
            {erro}
          </p>
        )}
        {pronto && (
          <p className="adm__aviso" role="status">
            Senha trocada. Quem mais estava dentro do painel saiu.
          </p>
        )}

        <button type="submit" className="adm__entrar" disabled={salvando}>
          {salvando ? 'Trocando…' : 'Trocar a senha'}
        </button>
      </form>
    </section>
  )
}

/**
 * Os lembretes de dose estão saindo?
 *
 * É a falha de maior consequência da Ruth e a mais silenciosa: se o beat ou o
 * worker param, nenhum lembrete sai, nada muda na tela de ninguém, e a mãe
 * descobre pela criança sem remédio. O batimento é escrito pela varredura de
 * verdade, então "ok" aqui quer dizer trabalho feito, não processo de pé.
 *
 * Fica no topo do painel porque é a única coisa desta tela que não pode
 * esperar. `/health/lembretes` é público de propósito — é o mesmo endereço que
 * um monitor de fora vigia, e monitor de fora é o que continua funcionando
 * quando a máquina inteira cai.
 */
function OsLembretes() {
  const [estado, setEstado] = useState<Batimento | null | undefined>(undefined)

  useEffect(() => {
    // 200 e 503 são as duas respostas desta rota, e as duas são notícia. O
    // resto — 404, 500, rede fora — não diz que os lembretes pararam; diz que
    // não deu para conferir, que é outra coisa. Gritar "não estão saindo" sem
    // saber seria a mesma mentira que passei o dia tirando da Ruth.
    const ver = () =>
      fetch('/health/lembretes')
        .then(async (r) => (r.ok || r.status === 503 ? ((await r.json()) as Batimento) : null))
        .then(setEstado)
        .catch(() => setEstado(null))

    void ver()
    const relogio = setInterval(ver, 60_000)
    return () => clearInterval(relogio)
  }, [])

  if (estado === undefined) return null

  if (estado === null) {
    return (
      <p className="adm__sub">Não consegui conferir os lembretes agora.</p>
    )
  }

  const parado = estado.status !== 'ok'
  const quando =
    estado.ha_minutos === 0
      ? 'agora há pouco'
      : estado.ha_minutos != null
        ? `há ${estado.ha_minutos} min`
        : 'nunca'

  // Só o problema é anunciado. Esta linha se refaz a cada minuto, e um
  // "está tudo bem" repetido em voz alta seria ruído para quem usa leitor de
  // tela — anúncio é para o que mudou e importa.
  return (
    <p className={parado ? 'adm__erro' : 'adm__aviso'} {...(parado ? { role: 'alert' } : {})}>
      {parado
        ? `Os lembretes de dose não estão saindo (última varredura: ${quando}). ` +
          'Enquanto isso não voltar, nenhuma mãe é avisada de remédio nem de compromisso.'
        : `Lembretes de dose: saindo normalmente (última varredura ${quando}).`}
    </p>
  )
}

/**
 * Uma casa na lista, e o caminho para apagá-la.
 *
 * Apagar some com tudo da mãe: plano, conversa, remédio, receita e a foto
 * dela. Não tem desfazer, então não é um botão só — é um botão que abre um
 * pedido de confirmação onde o e-mail dela tem que ser digitado. O servidor
 * confere de novo, porque uma rota que destrói não pode depender de a tela ter
 * perguntado.
 */
function LinhaDaCasa({
  casa,
  sessao,
  aoApagar,
}: {
  casa: Casa
  sessao: string
  aoApagar: () => Promise<void>
}) {
  const [confirmando, setConfirmando] = useState(false)
  const [digitado, setDigitado] = useState('')
  const [apagando, setApagando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const confere = digitado.trim().toLowerCase() === casa.email.trim().toLowerCase()

  const apagar = async () => {
    if (!confere || apagando) return
    setApagando(true)
    setErro(null)
    try {
      await chamar(`/casas/${casa.usuaria_id}?email=${encodeURIComponent(casa.email)}`, sessao, {
        method: 'DELETE',
      })
      await aoApagar()
    } catch (e) {
      setErro(frase(e, 'não consegui apagar'))
      setApagando(false)
    }
  }

  return (
    <div className="adm-casa">
      <span className="adm-casa__nome">{casa.nome_tratamento}</span>
      <span className="adm-casa__email">{casa.email}</span>
      <span className={`adm-casa__marca ${casa.se_apresentou ? 'adm-casa__marca--ok' : ''}`}>
        {casa.se_apresentou ? 'já se apresentou' : 'ainda não entrou'}
      </span>

      {confirmando ? (
        <div className="adm-casa__confirmar">
          <p className="adm-casa__aviso">
            Isto apaga tudo da {casa.nome_tratamento}: plano, conversa, remédios, receitas e as
            fotos. Não tem desfazer. Escreva o e-mail dela para confirmar.
          </p>
          <input
            type="email"
            autoComplete="off"
            placeholder={casa.email}
            aria-label={`E-mail da ${casa.nome_tratamento}, para confirmar`}
            value={digitado}
            onChange={(e) => setDigitado(e.target.value)}
          />
          <div className="adm-casa__acoes">
            <button
              type="button"
              className="adm-casa__apagar"
              disabled={!confere || apagando}
              onClick={() => void apagar()}
            >
              {apagando ? 'Apagando…' : 'Apagar de vez'}
            </button>
            <button
              type="button"
              className="adm__link"
              onClick={() => {
                setConfirmando(false)
                setDigitado('')
                setErro(null)
              }}
            >
              Cancelar
            </button>
          </div>
          {erro && <p className="adm__erro">{erro}</p>}
        </div>
      ) : (
        <button type="button" className="adm__link" onClick={() => setConfirmando(true)}>
          Apagar esta casa
        </button>
      )}
    </div>
  )
}

function Credencial({
  segredo,
  sessao,
  podeGuardar,
  aoMudar,
}: {
  segredo: Segredo
  sessao: string
  podeGuardar: boolean
  aoMudar: () => Promise<void>
}) {
  const [valor, setValor] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const guardar = async () => {
    if (!valor.trim()) return
    setSalvando(true)
    setErro(null)
    try {
      await chamar(`/segredos/${segredo.chave}`, sessao, {
        method: 'PUT',
        body: JSON.stringify({ valor }),
      })
      setValor('') // nada do que ela digitou fica na tela
      await aoMudar()
    } catch (e) {
      setErro(frase(e, 'não consegui guardar'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="adm-cred">
      <div className="adm-cred__topo">
        <span className="adm-cred__nome">{NOMES[segredo.chave] ?? segredo.chave}</span>
        <span className={`adm-cred__marca ${segredo.configurado ? 'adm-cred__marca--ok' : ''}`}>
          {segredo.configurado ? (
            <>
              <Icone nome="check" tamanho={14} /> {segredo.origem}
            </>
          ) : (
            'não configurada'
          )}
        </span>
      </div>

      <code className="adm-cred__variavel">{segredo.chave.toUpperCase()}</code>

      <div className="adm-cred__troca">
        <input
          type="password"
          autoComplete="off"
          placeholder={segredo.configurado ? 'digite a nova para trocar' : 'cole a credencial'}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          disabled={!podeGuardar || salvando}
        />
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={!podeGuardar || salvando || !valor.trim()}
        >
          {salvando ? '…' : 'Guardar'}
        </button>
      </div>

      {erro && <p className="adm__erro">{erro}</p>}

      {segredo.origem === 'banco' && (
        <button
          type="button"
          className="adm-cred__apagar"
          onClick={async () => {
            await chamar(`/segredos/${segredo.chave}`, sessao, { method: 'DELETE' })
            await aoMudar()
          }}
        >
          Apagar daqui e voltar a usar o .env
        </button>
      )}
    </div>
  )
}

/**
 * As casas na Ruth, e o convite de uma nova.
 *
 * A mãe entra convidada, não por cadastro aberto: a cobrança é Fase 2 e não há
 * teste grátis para segurar quem chega sozinho — um formulário público hoje
 * seria porta para robô criar casa, e cada casa gasta a chave da Black Belt.
 *
 * A lista não mostra nada do dia dela. Isto é operação, não bisbilhotice: o
 * que o operador precisa saber é quem foi convidada e quem ainda não entrou.
 */
function Casas({ sessao }: { sessao: string }) {
  const [casas, setCasas] = useState<Casa[] | null>(null)
  const [email, setEmail] = useState('')
  const [nome, setNome] = useState('')
  const [convidando, setConvidando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [link, setLink] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      setCasas((await chamar<{ casas: Casa[] }>('/casas', sessao)).casas)
    } catch (e) {
      setErro(frase(e, 'não consegui ler as casas'))
    }
  }, [sessao])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const convidar = async (evento: React.FormEvent) => {
    evento.preventDefault()
    setConvidando(true)
    setErro(null)
    setLink(null)
    try {
      const nova = await chamar<{ link: string | null }>('/casas', sessao, {
        method: 'POST',
        body: JSON.stringify({ email, nome_tratamento: nome }),
      })
      setEmail('')
      setNome('')
      // Sem SMTP configurado o e-mail não sai: o link aparece aqui para o
      // operador mandar à mão. Em produção o servidor não devolve link nenhum.
      setLink(nova.link)
      await carregar()
    } catch (e) {
      setErro(frase(e, 'não consegui convidar'))
    } finally {
      setConvidando(false)
    }
  }

  return (
    <section className="adm-casas">
      <h2 className="adm__secao">Casas</h2>

      {casas?.length === 0 && <p className="adm__sub">Ninguém ainda.</p>}

      {casas?.map((casa) => (
        <LinhaDaCasa key={casa.usuaria_id} casa={casa} sessao={sessao} aoApagar={carregar} />
      ))}

      <form className="adm-casas__nova" onSubmit={convidar}>
        <input
          type="text"
          placeholder="Como chamar ela"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="e-mail dela"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" disabled={convidando}>
          {convidando ? '…' : 'Convidar'}
        </button>
      </form>

      {link && (
        <p className="adm__aviso">
          Convite criado. Se o e-mail não sair, mande este link — ele vale 15 minutos e abre uma
          vez só: <code>{link}</code>
        </p>
      )}

      {erro && <p className="adm__erro">{erro}</p>}
    </section>
  )
}
